import { createFileRoute } from "@tanstack/react-router";
import type { InvoiceNote, InvoiceNoteScope } from "@somasi/shared";
import { invoiceNoteIsUnattached, richTextToPlain, toEditorHtml } from "@somasi/shared";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/integrations/aws/resources";
import { useConfirmDelete } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { RowAction, RowActions } from "@/components/row-actions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/rich-text-editor";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  BANK_ACCOUNT_PICKER_KEY,
  CLIENT_GROUP_PICKER_KEY,
  MultiEntitySelect,
  getBankAccountOption,
  getClientGroupOption,
  companyPickerKey,
  getCompanyOption,
  searchBankAccountOptions,
  searchClientGroupOptions,
  searchCompanyOptions,
  type BankAccountRow,
  type ClientGroupRow,
  type CompanyRow,
} from "@/components/entity-select";
import { BankAccountSelect, ClientGroupSelect, CompanySelect } from "@/components/entity-select";
import { Plus, Pencil, Trash2, StickyNote } from "lucide-react";
import { notify } from "@/lib/notify";
import { useAuth } from "@/lib/auth";
import {
  useRowSelection,
  BulkBar,
  SelectAllCheckbox,
  RowCheckbox,
} from "@/components/bulk-actions";
import {
  DragHandle,
  dropHintClass,
  FilterSelect,
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListSearch,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useDebounced,
  useDragReorder,
  useListSort,
  usePagination,
} from "@/components/list-controls";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/invoice-notes")({
  head: () => ({
    meta: [
      { title: "Invoice Notes — Somasi Ops" },
      {
        name: "description",
        content: "Reusable invoice notes and terms that can be attached to any Somasi invoice.",
      },
      { property: "og:title", content: "Invoice Notes — Somasi Ops" },
      {
        property: "og:description",
        content: "Reusable invoice notes and terms that can be attached to any Somasi invoice.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: InvoiceNotesPage,
});

/**
 * The filter's slices, and the column each one narrows.
 *
 * `scope` is the kind of attachment — "scoped to companies at all" — and the
 * column is what narrows that to one particular company. The server answers the
 * first (an array being non-empty is not a column filter) and the second is an
 * ordinary `contains`, so picking a company sends both.
 */
const SCOPE_LABEL: Record<InvoiceNoteScope, string> = {
  standard: "Standard — every invoice",
  company: "Attached to companies",
  client_group: "Attached to client groups",
  bank_account: "Attached to bank accounts",
  unattached: "Attached to nothing",
};

const SCOPE_COLUMN: Partial<Record<InvoiceNoteScope, keyof InvoiceNote>> = {
  company: "company_ids",
  client_group: "client_group_ids",
  bank_account: "bank_account_ids",
};

/** The attachment sets, as the row shows them — tolerant of older rows. */
function attachmentBadges(row: InvoiceNote): string[] {
  const counted = (ids: string[] | undefined, one: string, many: string) =>
    !ids?.length ? null : ids.length === 1 ? `1 ${one}` : `${ids.length} ${many}`;

  return [
    counted(row.company_ids, "company", "companies"),
    counted(row.client_group_ids, "group", "groups"),
    counted(row.bank_account_ids, "account", "accounts"),
  ].filter((text): text is string => text !== null);
}

/** One page of the list, as the query holds it. */
interface NotesPage {
  rows: InvoiceNote[];
  total: number;
}

function InvoiceNotesPage() {
  const { canEdit } = useAuth();
  const qc = useQueryClient();
  const confirmDelete = useConfirmDelete();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<InvoiceNote | null>(null);
  const [search, setSearch] = useState("");
  // "all" rather than "active": this is the maintenance screen, so a note
  // somebody marked inactive has to be findable here to be brought back.
  const [activeFilter, setActiveFilter] = useState("all");
  const [scope, setScope] = useState<"all" | InvoiceNoteScope>("all");
  /**
   * Which company, group or account the scope filter is narrowed to.
   *
   * Optional: picking "attached to companies" and nothing else asks which notes
   * are company-scoped at all, which is the question someone auditing the list
   * has. Cleared whenever the kind changes, since an id means nothing under a
   * different kind.
   */
  const [scopeId, setScopeId] = useState("");
  const scopeColumn = scope === "all" ? undefined : SCOPE_COLUMN[scope];
  const sort = useListSort({ column: "sort_order", ascending: true });
  // Held back so a search is one request when typing stops, not one per key.
  const query = useDebounced(search.trim());

  const filters = { query, activeFilter, scope, scopeId, sort: sort.sort };
  // Six data columns, plus the drag grip and the checkbox for an operator who can edit.
  const columnCount = canEdit ? 8 : 6;
  const pager = usePagination({ resetKey: filters });
  const queryKey = ["invoice_notes", { ...filters, page: pager.page, pageSize: pager.pageSize }];

  /** The list the filters describe. Filtering and sorting happen in the API. */
  const filteredList = () => {
    // `scope` is the API's own parameter, not a column filter — see the handler.
    const list = api.invoiceNotes.list(scope === "all" ? {} : { scope });

    if (activeFilter !== "all") list.eq("active", activeFilter === "active");
    if (scopeColumn && scopeId) list.contains(scopeColumn, scopeId);
    if (query) {
      // The body is searched as well as the label, because the wording is what
      // someone remembers — "bank charges" — not the short name it is filed under.
      list.or([
        { column: "label", op: "ilike", value: `%${query}%` },
        { column: "body", op: "ilike", value: `%${query}%` },
      ]);
    }

    list.order(sort.sort.column, { ascending: sort.sort.ascending });
    // `sort_order` is not unique, so without a tie-break two notes sharing a
    // position would swap places between requests — and a drag would then be
    // acting on an order the next fetch disagrees with.
    if (sort.sort.column !== "label") list.order("label");

    return list;
  };

  const { data: result, isLoading } = useQuery({
    queryKey,
    queryFn: async (): Promise<NotesPage> => {
      const { data, error, count } = await filteredList().page(pager.page, pager.pageSize);
      if (error) throw error;
      return { rows: (data ?? []) as InvoiceNote[], total: count ?? 0 };
    },
    placeholderData: (previous) => previous,
  });

  const data = result?.rows ?? [];
  const total = result?.total;

  const sel = useRowSelection(data);
  /**
   * The table, and the two caches the invoice screens read the notes through.
   *
   * React Query matches key *elements*, not string prefixes, so
   * `["invoice_notes"]` reaches neither `["invoice_notes_active"]` (the picker on
   * a new invoice) nor `["invoice_notes_all"]` (the bodies an invoice prints).
   * It matters more now than it did: attaching a note to a company changes which
   * invoices start with it, and a form left open would otherwise keep ticking
   * boxes by the old attachments.
   */
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["invoice_notes"] });
    qc.invalidateQueries({ queryKey: ["invoice_notes_active"] });
    qc.invalidateQueries({ queryKey: ["invoice_notes_all"] });
  };

  /** Shows the change at once; the refetch that follows is the authority. */
  const patchCache = (rows: InvoiceNote[]) =>
    qc.setQueryData<NotesPage>(queryKey, (previous) => previous && { ...previous, rows });

  const remove = async (row: InvoiceNote) => {
    if (!(await confirmDelete(`note “${row.label}”`))) return;
    const { error } = await api.invoiceNotes.remove(row.id);
    if (error) return notify.error(error);
    notify.success("Note deleted");
    refresh();
  };

  /**
   * The switch in the Status column: one `PATCH` of one column.
   *
   * It is the whole point of marking a note inactive rather than deleting it — the
   * invoices that already print it keep reading, while the picker on a new
   * invoice offers only the active ones.
   */
  const setActive = async (row: InvoiceNote, active: boolean) => {
    patchCache(data.map((note) => (note.id === row.id ? { ...note, active } : note)));

    const { error } = await api.invoiceNotes.update(row.id, { active });
    // The refetch below puts the switch back where it was if the write failed.
    if (error) notify.error(error);
    else notify.success(`“${row.label}” is now ${active ? "active" : "inactive"}`);
    refresh();
  };

  /**
   * Dragging only means something while the list is read in stored order.
   *
   * Sorted by label or by status, a drop would have to mean "renumber everything
   * to match this sort", which is a different act from moving one note — so the
   * grips go inert and say which sort brings them back.
   */
  const drag = useDragReorder({
    rows: data,
    enabled: canEdit && sort.sort.column === "sort_order" && sort.sort.ascending,
    onReorder: async (ids, next) => {
      // The positions this page's notes already held, handed back out in the new
      // order, so the Order column reads right straight away rather than showing
      // each note still carrying the number it was dragged away from. The server
      // renumbers the whole list densely and the refetch settles it.
      const slots = data.map((note) => note.sort_order);
      patchCache(
        next.map((note, index) => ({ ...note, sort_order: slots[index] ?? note.sort_order })),
      );

      const { error } = await api.invoiceNotes.reorder(ids);
      if (error) notify.error(error);
      refresh();
    },
  });

  return (
    <ListPage>
      <ListPageHeader>
        <PageHeader
          title="Invoice Notes"
          description="Wording an invoice can print, numbered in the order set here — drag a row's grip to reorder."
          actions={
            canEdit && (
              <Button
                onClick={() => {
                  setEditing(null);
                  setOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" /> New note
              </Button>
            )
          }
        />

        <ListToolbar>
          <ListSearch value={search} onChange={setSearch} placeholder="Search label or wording…" />
          <FilterSelect
            value={activeFilter}
            onChange={setActiveFilter}
            className="w-36"
            options={[
              { value: "all", label: "All" },
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
            ]}
          />
          <Select
            value={scope}
            onValueChange={(value) => {
              setScope(value as typeof scope);
              setScopeId("");
            }}
          >
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Anywhere</SelectItem>
              {(Object.keys(SCOPE_LABEL) as InvoiceNoteScope[]).map((value) => (
                <SelectItem key={value} value={value}>
                  {SCOPE_LABEL[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Narrows the chosen kind to one of them. Only the three attachment
              scopes have anything to narrow to. */}
          {scope === "company" && (
            <CompanySelect
              value={scopeId}
              onChange={setScopeId}
              clearable
              clearLabel="Any company"
              placeholder="Any company"
              className="w-64"
            />
          )}
          {scope === "client_group" && (
            <ClientGroupSelect
              value={scopeId}
              onChange={setScopeId}
              clearable
              clearLabel="Any client group"
              placeholder="Any client group"
              className="w-64"
            />
          )}
          {scope === "bank_account" && (
            <BankAccountSelect
              value={scopeId}
              onChange={setScopeId}
              clearable
              clearLabel="Any account"
              placeholder="Any account"
              // Wider than its siblings: an account is named by its beneficiary
              // and its bank together, since every one of them is in Somasi's
              // own name.
              className="w-72"
            />
          )}
        </ListToolbar>

        {canEdit && (
          <BulkBar
            sel={sel}
            resource={api.invoiceNotes}
            entityLabel="note"
            activeColumn="active"
            onDone={refresh}
          />
        )}
      </ListPageHeader>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              {canEdit && <TableHead className="w-10" />}
              {canEdit && (
                <TableHead className="w-10">
                  <SelectAllCheckbox sel={sel} />
                </TableHead>
              )}
              <SortHeader sort={sort} column="sort_order" className="w-16">
                Order
              </SortHeader>
              <SortHeader sort={sort} column="label">
                Label
              </SortHeader>
              <TableHead>Text printed on the invoice</TableHead>
              <TableHead className="w-48">Applies to</TableHead>
              <SortHeader sort={sort} column="active" className="w-28">
                Active
              </SortHeader>
              <TableHead className="w-px whitespace-nowrap text-right">
                {canEdit && "Actions"}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableMessageRow colSpan={columnCount}>Loading…</TableMessageRow>
            ) : data.length === 0 ? (
              <TableMessageRow colSpan={columnCount}>
                <StickyNote className="mx-auto mb-2 h-6 w-6 opacity-50" />
                {query || activeFilter !== "all" || scope !== "all"
                  ? "No notes match these filters."
                  : "No notes yet."}
              </TableMessageRow>
            ) : (
              data.map((row) => (
                <TableRow
                  key={row.id}
                  {...drag.rowProps(row.id)}
                  data-state={sel.isSelected(row.id) ? "selected" : undefined}
                  className={cn(
                    drag.isDragging(row.id) && "opacity-40",
                    dropHintClass(drag.dropHint(row.id)),
                  )}
                >
                  {canEdit && (
                    <TableCell>
                      <DragHandle drag={drag} id={row.id} label={row.label} />
                    </TableCell>
                  )}
                  {canEdit && (
                    <TableCell>
                      <RowCheckbox sel={sel} id={row.id} />
                    </TableCell>
                  )}
                  <TableCell className="text-muted-foreground tabular-nums">
                    {row.sort_order}
                  </TableCell>
                  <TableCell className="font-medium">{row.label}</TableCell>
                  {/* The wording is stored as markup now, so the cell shows what
                      it says rather than how it is marked up. `line-clamp` keeps a
                      long note from owning the row. */}
                  <TableCell className="max-w-xl text-sm text-muted-foreground">
                    <span className="line-clamp-3 whitespace-pre-line">
                      {richTextToPlain(row.body)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {row.is_standard && <Badge variant="secondary">Standard</Badge>}
                      {attachmentBadges(row).map((text) => (
                        <Badge key={text} variant="outline">
                          {text}
                        </Badge>
                      ))}
                      {/* Reaches no invoice by itself — worth saying, because it
                          is usually wording somebody meant to attach. */}
                      {!row.is_standard && invoiceNoteIsUnattached(row) && (
                        <span className="text-xs text-muted-foreground">On request only</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={row.active}
                        disabled={!canEdit}
                        aria-label={`Mark ${row.active ? "inactive" : "active"}: ${row.label}`}
                        onCheckedChange={(value) => setActive(row, value)}
                      />
                      <span className="text-xs text-muted-foreground">
                        {row.active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {canEdit && (
                      <RowActions>
                        <RowAction
                          label="Edit"
                          onClick={() => {
                            setEditing(row);
                            setOpen(true);
                          }}
                        >
                          <Pencil />
                        </RowAction>
                        <RowAction label="Delete" destructive onClick={() => remove(row)}>
                          <Trash2 />
                        </RowAction>
                      </RowActions>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination pagination={pager} total={total} loading={isLoading} unit="note" />
      </ListPageTable>

      {/* The next order number is one past the end of the whole list, not one
          past the page on screen. */}
      <NoteDialog
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        nextOrder={(total ?? 0) + 1}
        onSaved={refresh}
      />
    </ListPage>
  );
}

function NoteDialog({
  open,
  onOpenChange,
  editing,
  nextOrder,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: InvoiceNote | null;
  nextOrder: number;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    label: "",
    body: "",
    sort_order: nextOrder,
    active: true,
    is_standard: false,
    company_ids: [] as string[],
    client_group_ids: [] as string[],
    bank_account_ids: [] as string[],
  });

  useEffect(() => {
    if (open) {
      setForm({
        label: editing?.label ?? "",
        // A note written before the editor existed is plain text; it opens as
        // paragraphs so its line breaks survive, and saves as markup from then on.
        body: toEditorHtml(editing?.body),
        sort_order: editing?.sort_order ?? nextOrder,
        active: editing?.active ?? true,
        // A new note is attached to what it names; standard is the deliberate
        // choice, and the server defaults the same way.
        is_standard: editing?.is_standard ?? false,
        company_ids: editing?.company_ids ?? [],
        client_group_ids: editing?.client_group_ids ?? [],
        bank_account_ids: editing?.bank_account_ids ?? [],
      });
    }
  }, [open, editing, nextOrder]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    // An empty editor still emits `<p></p>`, so "is there anything here?" is a
    // question about the words rather than the markup.
    if (!form.label.trim() || !richTextToPlain(form.body).trim()) {
      return notify.error("Label and text are required");
    }
    setSaving(true);
    const payload = {
      label: form.label.trim(),
      body: form.body.trim(),
      sort_order: form.sort_order,
      active: form.active,
      is_standard: form.is_standard,
      company_ids: form.company_ids,
      client_group_ids: form.client_group_ids,
      bank_account_ids: form.bank_account_ids,
    };
    const { error } = editing
      ? await api.invoiceNotes.update(editing.id, payload)
      : await api.invoiceNotes.create(payload);
    setSaving(false);
    if (error) return notify.error(error);
    notify.success(editing ? "Note updated" : "Note added");
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit note" : "New invoice note"}</DialogTitle>
          <DialogDescription>
            e.g. "Subject to BVI jurisdiction" or "Payment should be net off bank charges".
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label required>Label</Label>
            <Input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Short name shown in the picker"
              required
            />
          </div>
          <div className="space-y-2">
            <Label required>Text printed on the invoice</Label>
            <RichTextEditor
              value={form.body}
              onChange={(body) => setForm({ ...form, body })}
              aria-label="Text printed on the invoice"
            />
            <p className="text-xs text-muted-foreground">
              Bold, italics, underlining and lists print on the invoice as they look here. Enter
              starts a new line; Shift+Enter breaks a line without one.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Display order</Label>
            <Input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
            />
            <p className="text-xs text-muted-foreground">
              Or leave it and drag the row into place on the list.
            </p>
          </div>
          <div className="space-y-3 rounded-md border p-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label>Standard note</Label>
                <p className="text-xs text-muted-foreground">
                  Starts ticked on every invoice. Leave it off and the note starts ticked only on
                  the invoices of whatever it is attached to below.
                </p>
              </div>
              <Switch
                checked={form.is_standard}
                onCheckedChange={(v) => setForm({ ...form, is_standard: v })}
              />
            </div>

            {/* The three sets are alternatives, so a note naming a group and an
                account starts ticked on an invoice matching either. */}
            <div className="space-y-3 border-t pt-3">
              <MultiEntitySelect<CompanyRow>
                label="Companies"
                values={form.company_ids}
                onChange={(ids) => setForm({ ...form, company_ids: ids })}
                queryKey={companyPickerKey()}
                search={(query) => searchCompanyOptions(query)}
                resolve={getCompanyOption}
                noun="companies"
                placeholder="Add a company…"
                searchPlaceholder="Search name or registration no.…"
                emptyText="No company matches that."
                emptyLabel="No company named."
              />
              <MultiEntitySelect<ClientGroupRow>
                label="Client groups"
                values={form.client_group_ids}
                onChange={(ids) => setForm({ ...form, client_group_ids: ids })}
                queryKey={CLIENT_GROUP_PICKER_KEY}
                search={searchClientGroupOptions}
                resolve={getClientGroupOption}
                noun="client groups"
                placeholder="Add a client group…"
                searchPlaceholder="Search client group…"
                emptyText="No client group matches that."
                emptyLabel="No client group named."
              />
              <MultiEntitySelect<BankAccountRow>
                label="Bank accounts"
                values={form.bank_account_ids}
                onChange={(ids) => setForm({ ...form, bank_account_ids: ids })}
                queryKey={BANK_ACCOUNT_PICKER_KEY}
                search={searchBankAccountOptions}
                resolve={getBankAccountOption}
                noun="accounts"
                placeholder="Add a bank account…"
                searchPlaceholder="Search account or bank…"
                emptyText="No account matches that."
                emptyLabel="No bank account named."
              />
            </div>

            {/* Saying it here beats someone finding out on the invoice that was
                supposed to carry it. */}
            {!form.is_standard &&
              invoiceNoteIsUnattached({
                company_ids: form.company_ids,
                client_group_ids: form.client_group_ids,
                bank_account_ids: form.bank_account_ids,
              }) && (
                <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                  Attached to nothing, so it will never start ticked — it stays available to tick by
                  hand on any invoice.
                </p>
              )}
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label>Active</Label>
              <p className="text-xs text-muted-foreground">
                Marking a note inactive takes it out of the picker; invoices already printing it keep it.
              </p>
            </div>
            <Switch
              checked={form.active}
              onCheckedChange={(v) => setForm({ ...form, active: v })}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
